import React, { FC, useLayoutEffect, useRef } from 'react';
import AppliedFilterChip, {
  OverflowCountChip,
} from '@components/AppliedFilterChip';
import { APPLIED_FILTER_CHIP_GAP } from '@constants/appliedFilterChips';
import { Box, Stack } from '@mui/material';
import {
  ModelFilterCategory,
  useModelFilterActions,
} from '@store/zustand/model-filters';
import {
  filtersPopoverOwnerProps,
  getFiltersPopoverState,
  useFiltersPopover,
  useFiltersPopoverActions,
} from '@views/Project/components/ModelFiltersPopover/filtersPopover.store';
import HiddenFiltersPopover from '@views/Project/components/Toolbox/components/AppliedFilterChips/HiddenFiltersPopover';
import useAppliedFilterChips from '@views/Project/components/Toolbox/components/AppliedFilterChips/useAppliedFilterChips';
import useChipRowOverflow from '@views/Project/components/Toolbox/components/AppliedFilterChips/useChipRowOverflow';

const gap = `${APPLIED_FILTER_CHIP_GAP}px`;

const noop = () => undefined;

type Props = {
  isLocked?: boolean;
};

/**
 * The single-row strip of applied-filter chips between the Filters funnel and
 * the kebab.
 *
 * Never wraps and never shows a partial chip: chips that do not fit collapse
 * from the right into a +N chip, recomputed on every resize and selection
 * change. A chip's ⓧ clears its whole category through the same store action
 * the category panel's Clear all uses, so the canvas, the badge and any open
 * drawer react identically, and it closes the popover when the panel showing
 * is that category's — the chip it was opened from is gone, and a panel of
 * unticked values anchored to the funnel is not what the ⓧ was asked for.
 * Clearing one category while another's panel is open leaves that one alone.
 * Clicking a chip opens the Filters popover on that
 * category, anchored beneath the chip. Only the row of chips itself is marked
 * as a popover owner — it is exactly as wide as its chips — so the pointer-down
 * of a chip click does not dismiss the popover first, while a click on the
 * blank toolbar strip beside the chips still does.
 *
 * Every chip is rendered twice — once hidden for measurement, once visible —
 * because a chip can be hidden from the moment it appears and must still be
 * measurable for the next widen. The measuring row's +N probe carries the
 * largest count the row could ever show, so the width reserved for it is
 * never too small. The visible row sits in an overflow-hidden container, which
 * keeps the toolbar height constant during the one pre-paint render in which
 * the counts may be stale. Because the measuring row is `visibility: hidden`
 * and comes first, no icon a chip renders may define an id: a `clipPath`,
 * mask or gradient defined here is the one the whole document resolves
 * `url(#id)` against, and a hidden `clipPath` child clips its referents away.
 *
 * The Filters popover may be anchored to one of these chips or to +N, and the
 * fit pass can unmount that element in a render nothing but this component
 * takes part in — a resize, or the second commit after a selection change. So
 * after every commit the row checks whether the popover's anchor is still in
 * the document and, if not, re-anchors it to wherever its category now lives:
 * the category's visible chip, the +N chip that holds it, or, when it has
 * left the row altogether, the funnel. The visible row's children are read by
 * position for that, the same way the measuring row is read for widths: the
 * visible chips in order, then the +N chip when any are hidden.
 *
 * Locked, the row is read-only: every chip is disabled and +N is a plain
 * disabled count rather than a popover, so nothing in the row can open the
 * Filters popover or clear a category. A disabled chip keeps its width, so
 * the fit pass needs no second measurement.
 */
const AppliedFilterChips: FC<Props> = ({ isLocked = false }) => {
  const chips = useAppliedFilterChips();
  const { clearCategory } = useModelFilterActions();
  const { openCategory, reanchorIfDetached, close } =
    useFiltersPopoverActions();
  const { anchorEl: openAnchorEl, category: shownCategory } =
    useFiltersPopover();
  const visibleRowRef = useRef<HTMLDivElement>(null);

  const clearChip = (category: ModelFilterCategory) => {
    const { anchorEl, category: shown } = getFiltersPopoverState();

    clearCategory(category);

    if (anchorEl && shown === category) {
      close();
    }
  };

  const measureKey = chips
    .map((chip) => `${chip.label}:${chip.summary}`)
    .join('|');
  const { containerRef, measureRowRef, visibleCount } = useChipRowOverflow(
    chips.length,
    measureKey,
  );

  useLayoutEffect(() => {
    const { anchorEl, category } = getFiltersPopoverState();

    if (!anchorEl || anchorEl.isConnected) {
      return;
    }

    const index = chips.findIndex((chip) => chip.category === category);
    const host =
      index === -1
        ? undefined
        : visibleRowRef.current?.children[Math.min(index, visibleCount)];

    reanchorIfDetached(host instanceof HTMLElement ? host : undefined);
  });

  if (chips.length === 0) {
    return null;
  }

  const visible = chips.slice(0, visibleCount);
  const hidden = chips.slice(visibleCount);

  return (
    <Box
      ref={containerRef}
      flex={1}
      minWidth={0}
      position="relative"
      overflow="hidden"
    >
      <Stack
        ref={measureRowRef}
        aria-hidden
        direction="row"
        gap={gap}
        width="max-content"
        position="absolute"
        top={0}
        left={0}
        visibility="hidden"
        sx={{ pointerEvents: 'none' }}
      >
        {chips.map((chip) => (
          <AppliedFilterChip
            key={chip.category}
            label={chip.label}
            summary={chip.summary}
            disabled={isLocked}
            onClick={noop}
            onClear={noop}
          />
        ))}
        <OverflowCountChip count={chips.length} />
      </Stack>

      <Stack
        ref={visibleRowRef}
        data-testid="applied-filter-chips"
        direction="row"
        alignItems="center"
        flexWrap="nowrap"
        gap={gap}
        width="max-content"
        {...filtersPopoverOwnerProps}
      >
        {visible.map((chip) => (
          <AppliedFilterChip
            key={chip.category}
            label={chip.label}
            summary={chip.summary}
            isActive={
              !isLocked &&
              Boolean(openAnchorEl) &&
              shownCategory === chip.category
            }
            disabled={isLocked}
            onClick={(event) =>
              openCategory(chip.category, event.currentTarget)
            }
            onClear={() => clearChip(chip.category)}
          />
        ))}

        {hidden.length > 0 &&
          (isLocked ? (
            <OverflowCountChip count={hidden.length} disabled />
          ) : (
            <HiddenFiltersPopover hidden={hidden} />
          ))}
      </Stack>
    </Box>
  );
};

export default AppliedFilterChips;
